import {
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  BarChart3,
  Check,
  EllipsisVertical,
  EyeOff,
  LineChart,
  Palette,
  PieChart,
  Type,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CHART_TYPES } from "../constants/cardRegistry";
import { usePreviewMode } from "../contexts/PreviewModeContext";

const CHART_TYPE_ICONS = {
  [CHART_TYPES.LINE]: LineChart,
  [CHART_TYPES.STRAIGHT_LINE]: LineChart,
  [CHART_TYPES.DOTTED_LINE]: LineChart,
  [CHART_TYPES.BAR]: BarChart3,
  [CHART_TYPES.PIE]: PieChart,
  [CHART_TYPES.BOLD_NUMBER]: Type,
};

const CHART_TYPE_LABELS = {
  [CHART_TYPES.LINE]: "chartActions.displayTypes.line",
  [CHART_TYPES.STRAIGHT_LINE]: "chartActions.displayTypes.straightLine",
  [CHART_TYPES.DOTTED_LINE]: "chartActions.displayTypes.dottedLine",
  [CHART_TYPES.BAR]: "chartActions.displayTypes.bar",
  [CHART_TYPES.PIE]: "chartActions.displayTypes.pie",
  [CHART_TYPES.BOLD_NUMBER]: "chartActions.displayTypes.boldNumber",
};

/**
 * Reusable 3-dot menu for chart cards.
 * In preview mode, "Hide" updates the preview template instead of SettingsContext.
 *
 * Props:
 * - onHide: callback to hide the card (normal mode)
 * - onColorChange: callback to open color picker
 * - chartType: current chart type
 * - allowedChartTypes: array of allowed CHART_TYPES for this card
 * - onChartTypeChange: callback(newType) — already preview-aware from page level
 * - cardKey: the card's registry key (for preview mode hide)
 * - configKey: "dashboard_config" | "analytics_config" | "asset_analytics_config"
 */
const CardChartMenu = ({
  onHide,
  onColorChange,
  chartType,
  allowedChartTypes = [],
  onChartTypeChange,
  cardKey,
  configKey,
}) => {
  const { t } = useTranslation("analytics");
  const [anchorEl, setAnchorEl] = useState(null);
  const { isPreviewMode, updateCardConfig } = usePreviewMode();

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleHide = () => {
    if (isPreviewMode && cardKey && configKey) {
      updateCardConfig(configKey, cardKey, { visible: false });
    } else if (onHide) {
      onHide();
    }
    handleClose();
  };

  const showDisplayTypes = allowedChartTypes.length > 1 && onChartTypeChange;

  return (
    <>
      <IconButton onClick={handleOpen} size="small">
        <EllipsisVertical size={16} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {(onHide || (isPreviewMode && cardKey)) && (
          <MenuItem onClick={handleHide} sx={{ py: 1 }}>
            <ListItemIcon>
              <EyeOff size={16} />
            </ListItemIcon>
            <ListItemText>{t("chartActions.hide")}</ListItemText>
          </MenuItem>
        )}
        {onColorChange && (
          <MenuItem
            onClick={() => {
              onColorChange();
              handleClose();
            }}
            sx={{ py: 1 }}
          >
            <ListItemIcon>
              <Palette size={16} />
            </ListItemIcon>
            <ListItemText>{t("chartActions.customizeColor")}</ListItemText>
          </MenuItem>
        )}
        {showDisplayTypes && (
          <>
            <Divider />
            {allowedChartTypes.map((type) => {
              const Icon = CHART_TYPE_ICONS[type] || LineChart;
              const isActive = type === chartType;
              return (
                <MenuItem
                  key={type}
                  onClick={() => {
                    onChartTypeChange(type);
                    handleClose();
                  }}
                  selected={isActive}
                  sx={{ py: 1 }}
                >
                  <ListItemIcon>
                    <Icon size={16} />
                  </ListItemIcon>
                  <ListItemText>{t(CHART_TYPE_LABELS[type])}</ListItemText>
                  {isActive && <Check size={14} />}
                </MenuItem>
              );
            })}
          </>
        )}
      </Menu>
    </>
  );
};

export default CardChartMenu;
